import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/** Max time (ms) to wait for Firebase auth before falling through to local-only mode */
const AUTH_TIMEOUT_MS = 3_000;

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isLoading, isAuthenticated } = useAuth();
  const [timedOut, setTimedOut] = useState(false);

  // If auth takes too long, stop blocking the UI
  useEffect(() => {
    if (!isLoading) return;

    const timer = setTimeout(() => {
      setTimedOut(true);
    }, AUTH_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [isLoading]);

  // Still loading, but haven't timed out yet — show spinner
  if (isLoading && !timedOut) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(to bottom, rgb(233,213,255), rgb(243,232,255), rgb(253,242,248))' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent" />
      </div>
    );
  }

  // Auth failed or timed out but not authenticated — render children anyway
  // (local-only mode: localStorage data still works, just no cloud sync)
  if (!isAuthenticated || timedOut) {
    return <>{children}</>;
  }

  return <>{children}</>;
};
